import { User } from 'user/User';
import { buildUser } from 'user/api/UserFactory';
import type { StudentDTO, UserDTO } from 'user/api/UserApi';
import { isLocale, Locales } from 'utils/enums';

const USER_TOKEN = 'USER_TOKEN';
const USER_DATA = 'USER_DATA';
const CURRENT_LOCALE = 'currentLocale';
const POST_ID = 'postID';
const ASSIGNMENT_REFERRAL_TOKEN = 'assignmentReferralToken';
const ASSIGNMENT_ID = 'assignmentId';
const TEACHING_PATH_REFERRAL_TOKEN = 'teachingPathReferralToken';
const TEACHING_PATH_ID = 'teachingPathId';
const ARTICLES_LOCALE_ID = 'articlesLocaleId';
const TAXONOMY_LOCALE_ID = 'taxonomyLocaleId';
const REDIRECTURL = 'REDIRECTURL';

export const STORAGE_INTERACTOR_KEY = 'STORAGE_INTERACTOR_KEY';

export class StorageInteractor {
  public setUserWithToken(token: string, user: object) {
    localStorage.setItem(USER_TOKEN, token);
    localStorage.setItem(USER_DATA, JSON.stringify(user));
  }

  public setUrlAfterLogin(url: string) {
    localStorage.setItem(REDIRECTURL, url);
  }

  public getUrlAfterLogin() {
    localStorage.getItem(REDIRECTURL);
  }

  public getUser(): User | null {
    const userDTO = localStorage.getItem(USER_DATA);
    if (!userDTO) {
      return null;
    }

    // Written by this application, so the shape is ours to name. It can still be corrupt, and
    // this runs while the login store is being constructed — a throw here would blank the page
    // with no boundary to catch it, where `null` means "logged out", which is what it is.
    try {
      return buildUser(JSON.parse(userDTO) as UserDTO & StudentDTO);
    } catch {
      return null;
    }
  }

  public getToken() {
    return localStorage.getItem(USER_TOKEN);
  }

  public getUserRole(): string | undefined {
    // JSON.parse returns `any`; the cast is the one place that fact is contained.
    const currentUser = JSON.parse(localStorage.getItem(USER_DATA)!) as { role?: string } | null;

    return currentUser?.role;
  }

  public logOut() {
    localStorage.removeItem(USER_TOKEN);
    localStorage.removeItem(USER_DATA);
  }

  /**
   * A `Locales` member, or null. Checked here rather than cast at each reader: two readers in
   * assignment/api.ts cast it `as Locales`, and four components compared the raw string with
   * enum members. App rewrites anything else to the default at startup, so in practice this
   * only ever turns a string into the member it already was.
   */
  public getCurrentLocale(): Locales | null {
    const stored = localStorage.getItem(CURRENT_LOCALE);

    return isLocale(stored) ? stored : null;
  }

  public getPostID() {
    return Number(localStorage.getItem(POST_ID));
  }

  public setPostID(postID: number) {
    localStorage.setItem(POST_ID, String(postID));
  }

  public removePostID() {
    localStorage.removeItem(POST_ID);
  }

  public getAssignmentReferralToken() {
    return localStorage.getItem(ASSIGNMENT_REFERRAL_TOKEN) || '';
  }

  public setAssignmentReferralToken(referralToken: string) {
    localStorage.setItem(ASSIGNMENT_REFERRAL_TOKEN, referralToken);
  }

  public removeAssignmentReferralToken() {
    localStorage.removeItem(ASSIGNMENT_REFERRAL_TOKEN);
  }

  public getTeachingPathReferralToken() {
    return localStorage.getItem(TEACHING_PATH_REFERRAL_TOKEN) || '';
  }

  public setTeachingPathReferralToken(referralToken: string) {
    localStorage.setItem(TEACHING_PATH_REFERRAL_TOKEN, referralToken);
  }

  public removeTeachingPathReferralToken() {
    localStorage.removeItem(TEACHING_PATH_REFERRAL_TOKEN);
  }

  public getAssignmentId() {
    return localStorage.getItem(ASSIGNMENT_ID) || '';
  }

  public setAssignmentId(assignmentId: string) {
    localStorage.setItem(ASSIGNMENT_ID, assignmentId);
  }

  public removeAssignmentId() {
    localStorage.removeItem(ASSIGNMENT_ID);
  }

  public getTeachingPathId() {
    return localStorage.getItem(TEACHING_PATH_ID) || '';
  }

  public setTeachingPathId(teachingPathId: string) {
    localStorage.setItem(TEACHING_PATH_ID, teachingPathId);
  }

  public removeTeachingPathId() {
    localStorage.removeItem(TEACHING_PATH_ID);
  }

  public getArticlesLocaleId() {
    return localStorage.getItem(ARTICLES_LOCALE_ID);
  }

  public getTaxonomyLocaleId() {
    return localStorage.getItem(TAXONOMY_LOCALE_ID);
  }

  public setArticlesLocaleId(locale: string) {
    localStorage.setItem(ARTICLES_LOCALE_ID, locale);
  }

  public setTaxonomyLocaleId(locale: string) {
    localStorage.setItem(TAXONOMY_LOCALE_ID, locale);
  }
}
